import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import useRealtimeReadOnlyList from '../../realtime/useRealtimeReadOnlyList';
import {
  formatAssemblyChangeLogField,
  formatAssemblyChangeLogValue,
  getSortedUniqueValues,
  normalizeAssemblyChangeLogs,
} from './assemblyScheduleChangeLogUtils';
import './AssemblyScheduleChangeLog.css';

const AssemblyScheduleChangeLog = ({
  apiKey = 'assemblyScheduleChangeLog',
  title = 'Assembly Schedule Change Log',
  description = 'Audit history for inline and modal edits made from the Assembly Schedule.',
  filterEntry,
  onRefresh,
}) => {
  const apiUrls = useSelector(selectApiUrls);
  const [userFilter, setUserFilter] = useState('');
  const [jobNumberFilter, setJobNumberFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useRealtimeReadOnlyList({ url: apiUrls?.[apiKey], live: apiKey === 'assemblyScheduleChangeLog' });

  const changeLogs = useMemo(() => normalizeAssemblyChangeLogs(data).filter((entry) => !filterEntry || filterEntry(entry)), [data, filterEntry]);
  const uniqueUsers = useMemo(
    () => getSortedUniqueValues(changeLogs, (item) => item.CurrentUser),
    [changeLogs]
  );
  const uniqueJobNumbers = useMemo(
    () => getSortedUniqueValues(changeLogs, (item) => item.JobNumber),
    [changeLogs]
  );
  const uniqueFields = useMemo(
    () => getSortedUniqueValues(changeLogs, (item) => formatAssemblyChangeLogField(item.Field)),
    [changeLogs]
  );
  const uniqueRoles = useMemo(
    () => getSortedUniqueValues(changeLogs, (item) => item.UserRole),
    [changeLogs]
  );

  const filteredLogs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const start = startDate ? moment.utc(startDate, 'YYYY-MM-DD').startOf('day') : null;
    const end = endDate ? moment.utc(endDate, 'YYYY-MM-DD').endOf('day') : null;

    return [...changeLogs]
      .sort((left, right) => new Date(right.DateTime) - new Date(left.DateTime))
      .filter((item) => !userFilter || item.CurrentUser === userFilter)
      .filter((item) => !jobNumberFilter || item.JobNumber === jobNumberFilter)
      .filter((item) => !fieldFilter || formatAssemblyChangeLogField(item.Field) === fieldFilter)
      .filter((item) => !roleFilter || item.UserRole === roleFilter)
      .filter((item) => !normalizedSearch || Object.values(item).some((value) => (
        String(value ?? '').toLowerCase().includes(normalizedSearch)
      )))
      .filter((item) => {
        if (!start && !end) return true;
        const itemDate = moment.utc(item.DateTime);
        if (!itemDate.isValid()) return false;
        if (start && itemDate.isBefore(start)) return false;
        if (end && itemDate.isAfter(end)) return false;
        return true;
      });
  }, [changeLogs, endDate, fieldFilter, jobNumberFilter, roleFilter, searchTerm, startDate, userFilter]);

  const errorMessage = error?.data?.message || error?.message || (error
    ? `Failed to fetch ${title.toLowerCase()}.`
    : '');
  const hasActiveFilters = Boolean(
    userFilter || jobNumberFilter || fieldFilter || roleFilter || searchTerm || startDate || endDate
  );

  const clearFilters = () => {
    setUserFilter('');
    setJobNumberFilter('');
    setFieldFilter('');
    setRoleFilter('');
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="assembly-change-log">
      <div className="assembly-change-log__header">
        <div>
          <h4>{title}</h4>
          <p>{description}</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="outline-secondary" size="sm" onClick={clearFilters} disabled={!hasActiveFilters}>
            Clear Filters
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => { refetch(); onRefresh?.(); }}
            disabled={isLoading || isFetching || !apiUrls?.[apiKey]}
          >
            {isLoading || isFetching ? (
              <><Spinner animation="border" size="sm" className="me-2" />Refreshing...</>
            ) : (
              <><i className="bi bi-arrow-repeat me-1" />Refresh</>
            )}
          </Button>
        </div>
      </div>

      <div className="assembly-change-log__filters">
        <Form>
          <Row className="g-3">
            <Col sm={6} xl={3}>
              <Form.Group controlId="assembly-change-user-filter">
                <Form.Label>User</Form.Label>
                <Form.Select size="sm" value={userFilter} onChange={(event) => setUserFilter(event.target.value)}>
                  <option value="">All users</option>
                  {uniqueUsers.map((user) => <option key={user} value={user}>{user}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6} xl={3}>
              <Form.Group controlId="assembly-change-job-filter">
                <Form.Label>Job number</Form.Label>
                <Form.Select size="sm" value={jobNumberFilter} onChange={(event) => setJobNumberFilter(event.target.value)}>
                  <option value="">All job numbers</option>
                  {uniqueJobNumbers.map((jobNumber) => (
                    <option key={jobNumber} value={jobNumber}>{jobNumber}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6} xl={3}>
              <Form.Group controlId="assembly-change-field-filter">
                <Form.Label>Field</Form.Label>
                <Form.Select size="sm" value={fieldFilter} onChange={(event) => setFieldFilter(event.target.value)}>
                  <option value="">All fields</option>
                  {uniqueFields.map((field) => <option key={field} value={field}>{field}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6} xl={3}>
              <Form.Group controlId="assembly-change-role-filter">
                <Form.Label>Role</Form.Label>
                <Form.Select size="sm" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                  <option value="">All roles</option>
                  {uniqueRoles.map((role) => <option key={role} value={role}>{role}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6} xl={3}>
              <Form.Group controlId="assembly-change-start-date">
                <Form.Label>Start date</Form.Label>
                <Form.Control type="date" size="sm" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
              </Form.Group>
            </Col>
            <Col sm={6} xl={3}>
              <Form.Group controlId="assembly-change-end-date">
                <Form.Label>End date</Form.Label>
                <Form.Control type="date" size="sm" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
              </Form.Group>
            </Col>
            <Col xl={6}>
              <Form.Group controlId="assembly-change-search">
                <Form.Label>Search</Form.Label>
                <Form.Control
                  size="sm"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search all audit fields..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="assembly-change-log__summary">
        Showing {filteredLogs.length} of {changeLogs.length} entries
      </div>

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      {isLoading ? (
        <div className="assembly-change-log__loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading {title.toLowerCase()}...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <Alert variant="info">
          {changeLogs.length === 0
            ? 'No changes found.'
            : 'No changes match the current filters.'}
        </Alert>
      ) : (
        <div className="assembly-change-log__table-wrapper">
          <Table striped bordered hover responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Date/Time</th>
                <th>User</th>
                <th>Role</th>
                <th>Job Number</th>
                <th>Job Name</th>
                <th>Field Changed</th>
                <th>Previous Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((item, index) => (
                <tr key={item.ID ?? item.Id ?? `${item.ProjectId ?? item.ProjectID}-${item.DateTime}-${item.Field}-${index}`}>
                  <td className="text-nowrap">{moment(item.DateTime).format('MM/DD/YYYY hh:mm A')}</td>
                  <td>{formatAssemblyChangeLogValue(item.CurrentUser)}</td>
                  <td>{formatAssemblyChangeLogValue(item.UserRole)}</td>
                  <td className="text-nowrap">{formatAssemblyChangeLogValue(item.JobNumber)}</td>
                  <td>{formatAssemblyChangeLogValue(item.JobName)}</td>
                  <td>{formatAssemblyChangeLogField(item.Field)}</td>
                  <td>{formatAssemblyChangeLogValue(item.PrevValue)}</td>
                  <td>{formatAssemblyChangeLogValue(item.NewValue)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AssemblyScheduleChangeLog;
